import React from 'react';

import { Box, Typography } from 'components';

import { useTranslation } from 'third-party';

export const PrivacyPolicy = () => {
  const { t } = useTranslation();

  return (
    <Box>
      <>
        <Box
          sx={{
            width: '100%',
            position: 'relative',
            paddingBottom: '0.3rem',
          }}
        >
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              width: '100%',
            }}
          >
            {t('legal.privacyPolicy.header')}
          </Typography>
        </Box>

        <Box sx={{ paddingBottom: '1rem', textAlign: 'center' }}>
          <Typography variant="subtitle1" sx={{ paddingTop: '0.8rem' }}>
            {t('legal.privacyPolicy.subHeader')}
          </Typography>
        </Box>

        <Box
          sx={{
            margin: '0 auto',
            padding: '1rem',
            maxHeight: '70vh',
            overflow: 'auto',
          }}
        >
          <Typography variant="body2" paragraph>
            EasyECheck.online is a financial technology platform that provides
            electronic check management tools. EasyECheck.online is not a bank
            or financial institution. We do not offer banking services or hold
            customer funds.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            PRIVACY NOTICE
          </Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2 }}>
            Last updated: July 6, 2025
          </Typography>
          <Typography variant="body2" paragraph>
            Thank you for choosing to be part of our community at EasyECheck LLC
            ("EasyECheck", "we", "us", or "our"). We are committed to protecting
            your personal information and your right to privacy. If you have any
            questions or concerns about this privacy notice or how we handle
            your information, please contact us at: support@easyecheck.online.
          </Typography>
          <Typography variant="body2" paragraph>
            This privacy notice explains what information we collect, how we use
            it, and what rights you have in relation to it. This applies to all
            personal data collected through our website
            https://easyecheck.online (the "Website"), and any associated
            services or interactions (collectively, the "Services").
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            1. WHAT INFORMATION DO WE COLLECT?
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 'medium', mt: 2 }}>
            Personal Information You Provide:
          </Typography>
          <Typography variant="body2" paragraph>
            We collect personal details you voluntarily provide to us, such as:
          </Typography>
          <Typography variant="body2" component="div" sx={{ ml: 2, mb: 2 }}>
            <ul>
              <li>Name, email address, phone number, mailing address</li>
              <li>
                Payment and billing information (credit card, bank account
                numbers)
              </li>
              <li>Account login details (e.g., username and password)</li>
              <li>Business information related to check transactions</li>
            </ul>
          </Typography>
          <Typography variant="body2" paragraph>
            We may also collect opt-in data such as SMS numbers for messaging
            purposes (with opt-out options available).
          </Typography>

          <Typography variant="body2" sx={{ fontWeight: 'medium', mt: 2 }}>
            Information Collected Automatically:
          </Typography>
          <Typography variant="body2" paragraph>
            When you access our Services, we automatically collect:
          </Typography>
          <Typography variant="body2" component="div" sx={{ ml: 2, mb: 2 }}>
            <ul>
              <li>
                IP address, browser/device info, access timestamps, and pages
                viewed
              </li>
              <li>
                Usage and log data for internal analytics and fraud prevention
              </li>
              <li>
                Cookies and similar technologies for personalization (see Cookie
                Notice)
              </li>
            </ul>
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            2. HOW DO WE USE YOUR INFORMATION?
          </Typography>
          <Typography variant="body2" paragraph>
            We use the data we collect for purposes such as:
          </Typography>
          <Typography variant="body2" component="div" sx={{ ml: 2, mb: 2 }}>
            <ul>
              <li>Account creation, authentication, and support</li>
              <li>Processing e-check-related transactions</li>
              <li>
                Communicating with users, including alerts or service updates
              </li>
              <li>Internal analytics to improve performance and features</li>
              <li>
                Marketing (only with consent; opt-out available at any time)
              </li>
              <li>
                Legal compliance, fraud detection, and security monitoring
              </li>
            </ul>
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            3. WILL YOUR INFORMATION BE SHARED?
          </Typography>
          <Typography variant="body2" paragraph>
            We do not sell your data. We may share your information with:
          </Typography>
          <Typography variant="body2" component="div" sx={{ ml: 2, mb: 2 }}>
            <ul>
              <li>
                Trusted service providers under confidentiality agreements
              </li>
              <li>Legal authorities when required by law</li>
              <li>
                Internal staff for customer support or technical administration
              </li>
            </ul>
          </Typography>
          <Typography variant="body2" paragraph>
            We never share or sell your phone number for SMS purposes. SMS
            opt-in is solely used for service notifications and may be opted out
            by replying "STOP".
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            4. COOKIES AND TRACKING
          </Typography>
          <Typography variant="body2" paragraph>
            We use cookies and similar tools for:
          </Typography>
          <Typography variant="body2" component="div" sx={{ ml: 2, mb: 2 }}>
            <ul>
              <li>Saving preferences</li>
              <li>Analyzing traffic and usage patterns</li>
              <li>Delivering relevant content or promotions</li>
            </ul>
          </Typography>
          <Typography variant="body2" paragraph>
            Users can manage or disable cookies via browser settings.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            5. SOCIAL LOGIN DATA
          </Typography>
          <Typography variant="body2" paragraph>
            If you register using services like Google or Facebook, we may
            access your public profile data (e.g., name, email). We only use
            this to support your login and account creation. Social platforms
            are responsible for their own data handling.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            6. DATA STORAGE AND TRANSFER
          </Typography>
          <Typography variant="body2" paragraph>
            We store and process data in the United States. If you are located
            elsewhere, please note that your data may be transferred
            internationally in accordance with privacy safeguards.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            7. THIRD-PARTY LINKS
          </Typography>
          <Typography variant="body2" paragraph>
            Our Website may contain links to third-party sites. We are not
            responsible for their privacy practices. Please review their privacy
            policies separately.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            8. DATA RETENTION
          </Typography>
          <Typography variant="body2" paragraph>
            We retain personal data only as long as necessary to:
          </Typography>
          <Typography variant="body2" component="div" sx={{ ml: 2, mb: 2 }}>
            <ul>
              <li>Fulfill services</li>
              <li>Comply with legal obligations</li>
              <li>Resolve disputes or enforce agreements</li>
            </ul>
          </Typography>
          <Typography variant="body2" paragraph>
            Inactive accounts may be deleted or anonymized after a reasonable
            period.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            9. DATA SECURITY
          </Typography>
          <Typography variant="body2" paragraph>
            We apply strict technical and organizational measures to protect
            your data. This includes encryption, secure storage, and access
            controls. However, no system can be 100% secure, and use of our
            Services is at your own risk.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            10. MINORS
          </Typography>
          <Typography variant="body2" paragraph>
            We do not knowingly collect data from anyone under 18. If we
            discover such data has been submitted, we will delete it promptly.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            11. YOUR PRIVACY RIGHTS
          </Typography>
          <Typography variant="body2" paragraph>
            You can:
          </Typography>
          <Typography variant="body2" component="div" sx={{ ml: 2, mb: 2 }}>
            <ul>
              <li>Access, correct, or delete your personal data</li>
              <li>Opt out of marketing emails or SMS messages</li>
              <li>Request account deletion</li>
            </ul>
          </Typography>
          <Typography variant="body2" paragraph>
            To exercise any of these rights, contact us at:
            support@easyecheck.online
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            12. DO-NOT-TRACK FEATURES
          </Typography>
          <Typography variant="body2" paragraph>
            Currently, our Website does not respond to browser "Do Not Track"
            signals.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            13. CHANGES TO THIS POLICY
          </Typography>
          <Typography variant="body2" paragraph>
            We may update this policy occasionally. When we do, we'll post a new
            "Last Updated" date and notify users of any significant changes.
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            14. CONTACT US
          </Typography>
          <Typography variant="body2" paragraph>
            Have questions about this privacy policy or your data?
          </Typography>
          <Typography variant="body2" paragraph>
            📧 Email: support@easyecheck.online
          </Typography>
        </Box>
      </>
    </Box>
  );
};
